import { useEffect, useState } from 'react';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { SearchBox } from '@/components/form/SearchBox';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useAsyncData } from '@/hooks/useAsyncData';
import { usePermissions } from '@/hooks/usePermissions';
import { showToast } from '@/lib/toast';
import {
  fetchRoles,
  fetchRole,
  fetchPermissionCatalog,
  createRole,
  updateRole,
  type RoleDetail,
} from '@/api/roles';
import { RoleForm } from './RoleForm';
import { RoleTile } from './RoleTile';

type FormMode = { mode: 'create' } | { mode: 'edit'; role: RoleDetail } | null;

/**
 * "Роли" — 1:1 старые Orchid RoleListScreen/RoleEditScreen (см. докблок
 * RoleController в Laravel): список с поиском по названию, просмотр и
 * редактирование роли вместе с её правами.
 *
 * Права на изменение ролей — то же 'platform.systems.roles', что и на
 * просмотр (в Orchid отдельного права на правку не было). Разделение
 * оставлено на будущее: canEdit считается отдельно, и без него форма
 * открывается в режиме просмотра.
 */
export function RolesPage() {
  const { can } = usePermissions();
  const canEdit = can('platform.systems.roles');

  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const [page, setPage] = useState(1);
  const [form, setForm] = useState<FormMode>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const editing = form?.mode === 'edit' ? form.role : null;

  useEffect(() => {
    setPage(1);
  }, [search]);

  // Открытая форма занимает всё место списка — грузить его в это время незачем.
  const {
    data: list,
    error,
    reload,
  } = useAsyncData(
    form ? null : () => fetchRoles({ search: search || undefined, page }),
    [search, page, form],
    'Не удалось загрузить роли.',
  );
  const rows = list?.items ?? null;
  const meta = list?.meta ?? null;

  // Без каталога плитка просто не покажет названия групп — ошибку не показываем.
  const { data: catalog } = useAsyncData(fetchPermissionCatalog, []);

  const openRole = async (id: number) => {
    try {
      setForm({ mode: 'edit', role: await fetchRole(id) });
    } catch {
      showToast('Не удалось открыть роль.');
    }
  };

  const title =
    form?.mode === 'create' ? 'Новая роль' : editing ? `Роль: ${editing.name}` : 'Роли';

  return (
    <section className="catalog">
      <header className="catalog__header catalog__header--row">
        <div>
          <h1>{title}</h1>
          <p className="catalog__lede">
            {form
              ? 'Разрешения и привилегии, доступные роли.'
              : 'Наборы прав, которые выдаются администраторам.'}
          </p>
        </div>

        {!form && canEdit && (
          <button type="button" className="btn btn--primary" onClick={() => setForm({ mode: 'create' })}>
            <Icon name="plus" size={15} /> Добавить роль
          </button>
        )}
      </header>

      <div className="card">
        {form?.mode === 'create' && (
          <RoleForm
            onCancel={() => setForm(null)}
            onSave={async (payload) => {
              const created = await createRole(payload);
              setForm(null);
              showToast(`Роль «${created.name}» создана`);
              reload();
            }}
          />
        )}

        {editing && (
          <RoleForm
            initial={editing}
            readOnly={!canEdit}
            onCancel={() => setForm(null)}
            onSave={async (payload) => {
              const updated = await updateRole(editing.id, payload);
              setForm(null);
              showToast(`Роль «${updated.name}» обновлена`);
              reload();
            }}
          />
        )}

        {!form && (
          <>
            <div className="filter-row">
              <SearchBox value={searchInput} onChange={setSearchInput} placeholder="Поиск по названию роли" />
            </div>

            {error && <p className="field-error">{error}</p>}

            {rows === null ? (
              <div className="panel-loading">
                <Spinner />
              </div>
            ) : rows.length === 0 ? (
              <p className="cell-muted">Роли не найдены.</p>
            ) : (
              <div className="catalog-tiles">
                {rows.map((row) => (
                  <RoleTile
                    key={row.id}
                    role={row}
                    catalog={catalog}
                    canEdit={canEdit}
                    expanded={expandedId === row.id}
                    onToggleExpand={() => setExpandedId((id) => (id === row.id ? null : row.id))}
                    onEditRequest={() => openRole(row.id)}
                    onDeleted={reload}
                  />
                ))}
              </div>
            )}

            {meta && meta.lastPage > 1 && (
              <div className="table-footer">
                <span className="table-footer__status">
                  Страница {meta.currentPage} из {meta.lastPage} · всего {meta.total}
                </span>
                <Pager page={meta.currentPage} lastPage={meta.lastPage} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
